import React, { useEffect, useMemo, useState } from "react";
// Hooks
import { useAppDispatch, useAppSelector } from "@/services/hooks";
// actions import
import {
  getHistoriesPagesByCurrentSite,
  deleteHistoryPageOperation,
  activateHistoryPageOperation,
} from "@/services/historiesPagesServices/historiesPagesAction";
// reactstrap imports
import { Button, Card, CardBody, CardHeader, Container } from "reactstrap";
import ReactBSAlert from "react-bootstrap-sweetalert";
import { ToastContainer } from "react-toastify";
import { useNavigate } from "react-router";
import NotificationAlertOptions from "@/layouts/Alerts";
import TableWrapper from "@/utils/TableWrapper";
import SimpleHeader from "@/components/Headers/SimpleHeader";

/**
 * A row of GET /api/historiespages for the current site. The template is a
 * populate that can resolve to `null` (SCRUM-1453, below), hence the nullable refs.
 */
type HistoryPageRow = {
  _id: string;
  updatedAt: string;
  global_title?: string;
  page_template?: { name?: string; type?: string } | null;
  page_configuration?: { type?: string; path?: string } | null;
};

type HistoryPageCellProps = { row: { original: HistoryPageRow } };

const DisplayHistoriesPages = (props: object) => {
  const dispatch = useAppDispatch();
  // SCRUM-691: subscribe to the histories_pages field, not the whole slice.
  const reduxHistoriesPages: HistoryPageRow[] = useAppSelector(
    (state) => state.historiespages.histories_pages
  );
  const [alert, setAlert] = useState<React.ReactNode>(null);
  const navigate = useNavigate();

  let notify = (type: string, msg: string) => {
    NotificationAlertOptions(type, msg);
  };

  useEffect(() => {
    dispatch(getHistoriesPagesByCurrentSite());
  }, [dispatch]);

  // SCRUM-1012: derive the sorted list straight from the store (single source of
  // truth, per the SCRUM-652 standard) instead of mirroring it into local state.
  // The old local copy was only re-synced while the redux list was non-empty, so
  // deleting/activating the last remaining row left it stale on screen until a
  // manual refresh. Sort by updatedAt descending (most recent first).
  const historiesPages = useMemo(
    () =>
      [...reduxHistoriesPages].sort(
        (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      ),
    [reduxHistoriesPages]
  );

  // Delete histories page — SCRUM-840: await .unwrap() so a failed delete
  // surfaces via the central hub instead of a silent unhandled rejection.
  // SCRUM-957: the "Page Deleted" success toast lives HERE, after the DELETE
  // resolves — it used to fire unconditionally from the confirm handler, so a
  // failed delete reported a false success. Mirrors activateHistoryPage below.
  const deleteHistoryPage = async (e: React.MouseEvent, page: HistoryPageRow) => {
    e.preventDefault();
    try {
      await dispatch(deleteHistoryPageOperation(page._id)).unwrap();
      notify("success", "Page Deleted");
    } catch {
      /* central hub surfaces the error */
    }
  };

  // make an updated page active
  const activateHistoryPage = async (e: React.MouseEvent, page: HistoryPageRow) => {
    e.preventDefault();
    try {
      // SCRUM-840: .unwrap() so the success toast only shows on success; the
      // central hub shows the error, so we drop our own (avoids a double toast).
      const result = await dispatch(
        activateHistoryPageOperation(page._id)
      ).unwrap();
      // SCRUM-1629: a non-administrator's activation is queued for review
      // (202, SCRUM-1627) -- say so in the server's words instead of
      // reporting an activation that did not happen.
      if (result.status === 202) {
        notify("info", result.msg || "Change submitted for admin review");
      } else {
        notify("success", "Page Activated!");
      }
    } catch {
      /* central hub surfaces the error */
    }
  };
  
  //Update history page
  const updateHistoryPage = (e: React.MouseEvent, page: HistoryPageRow) => {
    navigate("/admin/update-history-pg/" + page._id);
  };

  // Popup to confirm or not making a page delete
  const deleteAlert = (e: React.MouseEvent, element: HistoryPageRow) => {
    setAlert(
      // @ts-expect-error react-bootstrap-sweetalert's prop types reject the valid customIcon (ReactNode) / btnSize props this alert passes
      <ReactBSAlert
        custom
        style={{ display: "block", marginTop: "-100px" }}
        title="Are you sure to delete this version of the page?"
        customIcon={
          <div
            className="swal2-icon swal2-question swal2-animate-question-icon"
            style={{ display: "flex" }}
          >
            <span className="swal2-icon-text">?</span>
          </div>
        }
        onConfirm={() => {
          deleteHistoryPage(e, element);
          hideAlert();
        }}
        onCancel={() => hideAlert()}
        confirmBtnBsStyle="danger"
        confirmBtnText="Ok"
        btnSize=""
      >
        Delete Page Operation
      </ReactBSAlert>
    );
  };

  // Popup to confirm or not making a page active
  const questionAlert = (e: React.MouseEvent, element: HistoryPageRow) => {
    setAlert(
      // @ts-expect-error react-bootstrap-sweetalert's prop types reject the valid customIcon (ReactNode) / btnSize props this alert passes
      <ReactBSAlert
        custom
        style={{ display: "block", marginTop: "-100px" }}
        title="Would you like to publish this version?  "
        customIcon={
          <div
            className="swal2-icon swal2-question swal2-animate-question-icon"
            style={{ display: "flex" }}
          >
            <span className="swal2-icon-text">?</span>
          </div>
        }
        onConfirm={() => {
          activateHistoryPage(e, element);
          hideAlert();
        }}
        onCancel={() => hideAlert()}
        confirmBtnBsStyle="default"
        confirmBtnText="Ok"
        btnSize=""
      >
        Publish Operation
      </ReactBSAlert>
    );
  };

  const hideAlert = () => {
    setAlert(null);
  };

  // SCRUM-1453: these four accessors used to dereference `page_template` and
  // `page_configuration` bare. `getHistoriesPagesBySite` populates the template
  // without filtering (server/controllers/historiespages.js:282-285), and a
  // populate of a ref that resolves to nothing yields `null` — so a single such
  // row threw here during render, React unmounted the tree, and the screen went
  // blank. That took the admin's only Activate control with it, leaving an edit
  // to a published page saved but unpublishable.
  //
  // The rows are guarded, not filtered: a page whose template is gone is exactly
  // the one an editor still needs to reach. Same shape as the sibling list at
  // Display/PagesContainer.jsx:93-96, which renders these documents today.
  const columns = [
    {
      accessorKey: 'global_title',
      header: 'Meta Page Title',
    },
    {
      accessorFn: (row: HistoryPageRow) => row.page_template?.name || '-',
      header: 'Template Name',
    },
    {
      accessorFn: (row: HistoryPageRow) => row.page_template?.type || '-',
      header: 'Template Type',
    },
    {
      accessorFn: (row: HistoryPageRow) => row.page_configuration?.type || '-',
      header: 'Page Type',
    },
    {
      accessorFn: (row: HistoryPageRow) => row.page_configuration?.path || '-',
      header: 'Routing Path',
    },
    {
      header: 'ACTION',
      cell: ({ row }: HistoryPageCellProps) => (
        <div>
          <Button
            color="danger"
            size="sm"
            id={"buttondelet" + row.original._id}
            onClick={(e) => deleteAlert(e, row.original)}
          >
            <span className="btn-inner--icon mr-1">
              <i className="fas fa-trash" />
            </span>
            <span className="btn-inner--text">Delete</span>
          </Button>

          <Button
            id={"btnconfig" + row.original._id}
            color="default"
            size="sm"
            onClick={(e) => updateHistoryPage(e, row.original)}
          >
            <span className="btn-inner--icon mr-1">
              <i className="ni ni-settings-gear-65" />
            </span>
            <span className="btn-inner--text">Edit</span>
          </Button>

          <Button
            id={"btnPublish" + row.original._id}
            color="info"
            size="sm"
            onClick={(e) => questionAlert(e, row.original)}
          >
            <span className="btn-inner--icon mr-1">
              <i className="ni ni-curved-next" />
            </span>
            <span className="btn-inner--text">Activate</span>
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="rna-wrapper">
        <ToastContainer />
      </div>
      {alert}
      <SimpleHeader name="Pending Pages" parentName="Histories" />
      <Container className="mt--6" fluid>
        <Card>
          <CardHeader>
            <h3 className="mb-0">Manage Histories Pages</h3>
          </CardHeader>
          <CardBody>
            {historiesPages.length > 0 ? (
              <TableWrapper
                data={historiesPages}
                columns={columns}
              />
            ) : (
              <div>No histories pages available.</div>
            )}
          </CardBody>
        </Card>
      </Container>
    </>
  );
};

export default DisplayHistoriesPages;